import axios, {
	AxiosError,
	AxiosInstance,
	CreateAxiosDefaults,
	HttpStatusCode,
	InternalAxiosRequestConfig,
} from 'axios';
import cookies from 'js-cookie';

import { updateToken } from '@/api/auth';
import { REFRESH_TOKEN_NAME, TOKEN_COOKIE_NAME, USER_LOGGED_KEY_NAME } from '@/auth/constants';
import { Environment, isGrafanaEnabledEnvironment } from '@/constants/environments';
import { LocalStorage } from '@/utils';
import { faro } from '@grafana/faro-web-sdk';
import * as Sentry from '@sentry/nextjs';

type RequestMetadata = {
	startedAt: number;
};

type InstrumentedRequestConfig = InternalAxiosRequestConfig & {
	metadata?: RequestMetadata;
	skipFaroMeasurement?: boolean;
};

type RequestServiceConfig = {
	axiosConfig?: CreateAxiosDefaults;
	isServerSide?: boolean;
};

const DefaultRequestConfig: RequestServiceConfig = {
	axiosConfig: {},
	isServerSide: false,
} as const;

export class RequestService {
	private static instance: AxiosInstance;
	private static refreshing: boolean = false;
	private static interceptorAttached: boolean = false;

	public static getInstance(config: RequestServiceConfig = DefaultRequestConfig) {
		if (!RequestService.instance) {
			RequestService.instance = axios.create({
				baseURL: process.env.HOST,
				...(config.axiosConfig ?? {}),
			});
		}
		return RequestService.instance;
	}

	public static setAuthHeader(value: string) {
		if (!RequestService.instance) {
			throw new Error('Axios instance has not been created');
		}
		RequestService.instance.defaults.headers['Authorization'] = `Bearer ${value}`;
	}

	public static clearAuthHeader() {
		if (!RequestService.instance) {
			return;
		}
		delete RequestService.instance.defaults.headers['Authorization'];
	}

	public static addInterceptor() {
		if (!RequestService.instance || RequestService.interceptorAttached) {
			return;
		}

		RequestService.instance.interceptors.request.use((config: InstrumentedRequestConfig) => {
			if (typeof window !== 'undefined') {
				config.metadata = {
					startedAt: performance.now(),
				};
			}

			return config;
		});

		RequestService.instance.interceptors.response.use(
			(response) => {
				RequestService.reportRequest({
					config: response.config as InstrumentedRequestConfig,
					status: response.status,
					result: 'success',
				});

				return response;
			},
			async (error: AxiosError) => {
				if (Environment.Prod === (process.env.ENVIRONMENT as Environment)) {
					RequestService.reportRequest({
						config: error.config,
						status: error.response?.status ?? 0,
						result: RequestService.getErrorResult(error),
					});
				}

				try {
					if (
						[Environment.Prod, Environment.Preprod].includes(process.env.ENVIRONMENT as Environment)
					) {
						Sentry.withScope((scope) => {
							scope.setTag('api_error', 'interceptor');
							scope.setLevel('error');

							Sentry.captureException(error);
						});
					}
				} catch {}

				if (error?.response?.status === HttpStatusCode.Unauthorized) {
					if (typeof window === 'undefined') {
						return Promise.reject(error);
					}

					if (error.config.url.includes('refresh')) {
						cookies.remove(TOKEN_COOKIE_NAME);
						cookies.remove(REFRESH_TOKEN_NAME);
						LocalStorage.removeItem(USER_LOGGED_KEY_NAME);

						return Promise.reject(error);
					}
					if (RequestService.refreshing) {
						return Promise.reject(error);
					}
					RequestService.refreshing = true;
					try {
						const { access } = await updateToken();
						RequestService.setAuthHeader(access);

						cookies.set(TOKEN_COOKIE_NAME, access);
						LocalStorage.setItem(USER_LOGGED_KEY_NAME, 'true');

						return RequestService.instance.request({
							...error.config,
							headers: {
								...error.config.headers,
								Authorization: `Bearer ${access}`,
							},
						});
					} catch (e) {
						cookies.remove(TOKEN_COOKIE_NAME);
						cookies.remove(REFRESH_TOKEN_NAME);
						LocalStorage.removeItem(USER_LOGGED_KEY_NAME);

						return Promise.reject(e);
					} finally {
						RequestService.refreshing = false;
					}
				}

				return Promise.reject(error);
			},
		);

		RequestService.interceptorAttached = true;
	}

	private static reportRequest({
		config,
		status,
		result,
	}: {
		config?: InstrumentedRequestConfig;
		status: number;
		result: string;
	}) {
		if (
			typeof window === 'undefined' ||
			!isGrafanaEnabledEnvironment(process.env.ENVIRONMENT) ||
			!config?.metadata ||
			config.skipFaroMeasurement
		) {
			return;
		}

		const durationMs = performance.now() - config.metadata.startedAt;

		faro.api.pushMeasurement(
			{
				type: 'api_request',
				values: {
					duration_ms: durationMs,
				},
			},
			{
				context: {
					method: config.method?.toUpperCase() ?? 'UNKNOWN',
					route: RequestService.normalizeRoute(config.url ?? ''),
					status: String(status),
					result,
				},
			},
		);
	}

	private static normalizeRoute(url: string): string {
		const pathname = url.replace(/^(?:https?:)?\/\/[^/]+/i, '').split('?')[0];

		return pathname
			.replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,36}(?=\/|$)/gi, '/:id')
			.replace(/\/\d+(?=\/|$)/g, '/:id');
	}

	private static getErrorResult(error: AxiosError): string {
		if (axios.isCancel(error)) {
			return 'cancelled';
		}

		if (!error.response) {
			return 'network_error';
		}

		return 'http_error';
	}
}
